import { fetchApi } from './client';
import type * as T from './types';
import type { FormSection, FormSubject, VerificationForm } from '../features/verificationForms/types';

// ── Auth ──────────────────────────────────────────────────────────────────────
export const auth = {
  login: (email: string, password: string): Promise<T.LoginResponse> =>
    fetchApi('/api/auth/login', { method: 'POST', body: JSON.stringify({ email, password }) }),
  me: (): Promise<T.Admin> => fetchApi<{ admin: T.Admin }>('/api/auth/me').then((r) => r.admin),
  logout: (): Promise<{ success: boolean }> =>
    fetchApi('/api/auth/logout', { method: 'POST' }),
  /** Emails a reset link; the reply is the same for any address. */
  forgotPassword: (email: string): Promise<{ success: boolean }> =>
    fetchApi('/api/auth/forgot-password', { method: 'POST', body: JSON.stringify({ email }) }),
  resetPassword: (token: string, password: string): Promise<{ success: boolean }> =>
    fetchApi('/api/auth/reset-password', { method: 'POST', body: JSON.stringify({ token, password }) }),
  /** Change your own password. Requires the current one. */
  changePassword: (currentPassword: string, newPassword: string): Promise<{ success: boolean }> =>
    fetchApi('/api/auth/password', {
      method: 'POST',
      body: JSON.stringify({ currentPassword, newPassword }),
    }),
};

// ── Talents ───────────────────────────────────────────────────────────────────
export const talents = {
  list: (params: { search?: string; verification?: string; account_status?: string; category?: string; page?: number; limit?: number }): Promise<T.PaginatedResponse<T.Talent>> => {
    const qs = new URLSearchParams();
    if (params.search) qs.set('search', params.search);
    if (params.verification) qs.set('verification', params.verification);
    if (params.account_status) qs.set('account_status', params.account_status);
    if (params.category) qs.set('category', params.category);
    if (params.page) qs.set('page', String(params.page));
    if (params.limit) qs.set('limit', String(params.limit));
    return fetchApi(`/api/talents?${qs}`);
  },
  get: (id: string): Promise<T.Talent> => fetchApi(`/api/talents/${id}`),
  update: (id: string, data: Partial<T.Talent>): Promise<T.Talent> =>
    fetchApi(`/api/talents/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  remove: (id: string): Promise<{ success: boolean }> =>
    fetchApi(`/api/talents/${id}`, { method: 'DELETE' }),
  getApplications: (id: string): Promise<T.Application[]> => fetchApi(`/api/talents/${id}/applications`),
  getPayments: (id: string): Promise<T.Payment[]> => fetchApi(`/api/talents/${id}/payments`),
};

// ── Orgs ──────────────────────────────────────────────────────────────────────
export const orgs = {
  list: (params: { search?: string; kyb_status?: string; account_status?: string; industry?: string; page?: number; limit?: number }): Promise<T.PaginatedResponse<T.Org>> => {
    const qs = new URLSearchParams();
    if (params.search) qs.set('search', params.search);
    if (params.kyb_status) qs.set('kyb_status', params.kyb_status);
    if (params.account_status) qs.set('account_status', params.account_status);
    if (params.industry) qs.set('industry', params.industry);
    if (params.page) qs.set('page', String(params.page));
    if (params.limit) qs.set('limit', String(params.limit));
    return fetchApi(`/api/orgs?${qs}`);
  },
  get: (id: string): Promise<T.Org> => fetchApi(`/api/orgs/${id}`),
  update: (id: string, data: Partial<T.Org>): Promise<T.Org> =>
    fetchApi(`/api/orgs/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  remove: (id: string): Promise<{ success: boolean }> =>
    fetchApi(`/api/orgs/${id}`, { method: 'DELETE' }),
  getPostings: (id: string): Promise<T.Posting[]> => fetchApi(`/api/orgs/${id}/postings`),
  getPayments: (id: string): Promise<T.Payment[]> => fetchApi(`/api/orgs/${id}/payments`),
};

// ── Postings ──────────────────────────────────────────────────────────────────
export const postings = {
  list: (params: { search?: string; status?: string; org_id?: string; category?: string; date_from?: string; date_to?: string; page?: number; limit?: number }): Promise<T.PaginatedResponse<T.Posting>> => {
    const qs = new URLSearchParams();
    if (params.search) qs.set('search', params.search);
    if (params.status) qs.set('status', params.status);
    if (params.org_id) qs.set('org_id', params.org_id);
    if (params.category) qs.set('category', params.category);
    if (params.date_from) qs.set('date_from', params.date_from);
    if (params.date_to) qs.set('date_to', params.date_to);
    if (params.page) qs.set('page', String(params.page));
    if (params.limit) qs.set('limit', String(params.limit));
    return fetchApi(`/api/postings?${qs}`);
  },
  get: (id: string): Promise<T.Posting> => fetchApi(`/api/postings/${id}`),
  create: (data: Partial<T.Posting>): Promise<T.Posting> =>
    fetchApi('/api/postings', { method: 'POST', body: JSON.stringify(data) }),
  update: (id: string, data: Partial<T.Posting>): Promise<T.Posting> =>
    fetchApi(`/api/postings/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  remove: (id: string): Promise<{ success: boolean }> =>
    fetchApi(`/api/postings/${id}`, { method: 'DELETE' }),
};

// ── Applications ──────────────────────────────────────────────────────────────
export const applications = {
  list: (params: { search?: string; status?: string; org_id?: string; category?: string; date_from?: string; date_to?: string; page?: number; limit?: number }): Promise<T.PaginatedResponse<T.Application>> => {
    const qs = new URLSearchParams();
    if (params.search) qs.set('search', params.search);
    if (params.status) qs.set('status', params.status);
    if (params.org_id) qs.set('org_id', params.org_id);
    if (params.category) qs.set('category', params.category);
    if (params.date_from) qs.set('date_from', params.date_from);
    if (params.date_to) qs.set('date_to', params.date_to);
    if (params.page) qs.set('page', String(params.page));
    if (params.limit) qs.set('limit', String(params.limit));
    return fetchApi(`/api/applications?${qs}`);
  },
  get: (id: string): Promise<T.Application> => fetchApi(`/api/applications/${id}`),
  update: (id: string, data: { status: string }): Promise<T.Application> =>
    fetchApi(`/api/applications/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
};

// ── Payments ──────────────────────────────────────────────────────────────────
export const payments = {
  list: (params: { search?: string; status?: string; org_id?: string; date_from?: string; date_to?: string; page?: number; limit?: number }): Promise<T.PaginatedResponse<T.Payment>> => {
    const qs = new URLSearchParams();
    if (params.search) qs.set('search', params.search);
    if (params.status) qs.set('status', params.status);
    if (params.org_id) qs.set('org_id', params.org_id);
    if (params.date_from) qs.set('date_from', params.date_from);
    if (params.date_to) qs.set('date_to', params.date_to);
    if (params.page) qs.set('page', String(params.page));
    if (params.limit) qs.set('limit', String(params.limit));
    return fetchApi(`/api/payments?${qs}`);
  },
  get: (id: string): Promise<T.Payment> => fetchApi(`/api/payments/${id}`),
  /**
   * Release or refund the escrow behind this payment. The admin backend proxies
   * it to the app API, which owns the escrow rules — so a rejection here ("only
   * escrow that is still held can be refunded") is the platform's own answer.
   */
  update: (id: string, data: { status: 'Released' | 'Refunded'; reason?: string }): Promise<T.Payment> =>
    fetchApi(`/api/payments/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
};

// ── Verification ──────────────────────────────────────────────────────────────
export const verification = {
  list: (params: { entity_type?: string; priority?: string; assigned_to?: string; page?: number; limit?: number }): Promise<T.PaginatedResponse<T.VerificationItem>> => {
    const qs = new URLSearchParams();
    if (params.entity_type) qs.set('entity_type', params.entity_type);
    if (params.priority) qs.set('priority', params.priority);
    if (params.assigned_to) qs.set('assigned_to', params.assigned_to);
    if (params.page) qs.set('page', String(params.page));
    if (params.limit) qs.set('limit', String(params.limit));
    return fetchApi(`/api/verification?${qs}`);
  },
  get: (id: string): Promise<T.VerificationItem> => fetchApi(`/api/verification/${id}`),
  assign: (id: string, assignedTo: string): Promise<T.VerificationItem> =>
    fetchApi(`/api/verification/${id}`, { method: 'PATCH', body: JSON.stringify({ assigned_to: assignedTo }) }),
  approve: (id: string): Promise<{ success: boolean }> =>
    fetchApi(`/api/verification/${id}/approve`, { method: 'POST' }),
  reject: (id: string, notes: string): Promise<{ success: boolean }> =>
    fetchApi(`/api/verification/${id}/reject`, { method: 'POST', body: JSON.stringify({ notes }) }),
  /** Path (not a fetch call) for AuthenticatedImage — the response is a binary image, not JSON. */
  mediaPath: (documentUrl: string): string => `/api/verification/media?url=${encodeURIComponent(documentUrl)}`,
};

// ── Reports (Moderation) ──────────────────────────────────────────────────────
export const reports = {
  list: (params: { status?: string; severity?: string; reported_entity_type?: string; page?: number; limit?: number }): Promise<T.PaginatedResponse<T.Report>> => {
    const qs = new URLSearchParams();
    if (params.status) qs.set('status', params.status);
    if (params.severity) qs.set('severity', params.severity);
    if (params.reported_entity_type) qs.set('reported_entity_type', params.reported_entity_type);
    if (params.page) qs.set('page', String(params.page));
    if (params.limit) qs.set('limit', String(params.limit));
    return fetchApi(`/api/moderation/reports?${qs}`);
  },
  get: (id: string): Promise<T.Report> => fetchApi(`/api/moderation/reports/${id}`),
  update: (id: string, data: Partial<T.Report>): Promise<T.Report> =>
    fetchApi(`/api/moderation/reports/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  create: (data: {
    reported_entity_type: string;
    reported_entity_id: string;
    reported_entity_name?: string;
    reason: string;
    description?: string;
    severity?: string;
  }): Promise<T.Report> =>
    fetchApi('/api/moderation/reports', { method: 'POST', body: JSON.stringify(data) }),
  /** warn / suspend the reported account, or dismiss the report. */
  enforce: (id: string, action: 'warn' | 'suspend' | 'dismiss', note?: string): Promise<{ success: boolean }> =>
    fetchApi(`/api/moderation/reports/${id}/enforce`, { method: 'POST', body: JSON.stringify({ action, note }) }),
};

// ── Categories ────────────────────────────────────────────────────────────────
export const categories = {
  list: (params?: { active?: boolean }): Promise<T.Category[]> => {
    const qs = new URLSearchParams();
    if (params?.active !== undefined) qs.set('active', String(params.active));
    return fetchApi(`/api/categories${qs.toString() ? '?' + qs : ''}`);
  },
  get: (id: string): Promise<T.Category & { questions: T.OnboardingQuestion[] }> => fetchApi(`/api/categories/${id}`),
  create: (data: { name: string; slug?: string; active?: boolean }): Promise<T.Category> =>
    fetchApi('/api/categories', { method: 'POST', body: JSON.stringify(data) }),
  update: (id: string, data: Partial<T.Category>): Promise<T.Category> =>
    fetchApi(`/api/categories/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  remove: (id: string): Promise<{ success: boolean }> =>
    fetchApi(`/api/categories/${id}`, { method: 'DELETE' }),
  addQuestion: (categoryId: string, data: Partial<T.OnboardingQuestion>): Promise<T.OnboardingQuestion> =>
    fetchApi(`/api/categories/${categoryId}/questions`, { method: 'POST', body: JSON.stringify(data) }),
  updateQuestion: (categoryId: string, qid: string, data: Partial<T.OnboardingQuestion>): Promise<T.OnboardingQuestion> =>
    fetchApi(`/api/categories/${categoryId}/questions/${qid}`, { method: 'PATCH', body: JSON.stringify(data) }),
  removeQuestion: (categoryId: string, qid: string): Promise<{ success: boolean }> =>
    fetchApi(`/api/categories/${categoryId}/questions/${qid}`, { method: 'DELETE' }),
};

// ── Announcements ─────────────────────────────────────────────────────────────
export const announcements = {
  list: (params?: { status?: string }): Promise<T.Announcement[]> => {
    const qs = new URLSearchParams();
    if (params?.status) qs.set('status', params.status);
    return fetchApi(`/api/announcements${qs.toString() ? '?' + qs : ''}`);
  },
  get: (id: string): Promise<T.Announcement> => fetchApi(`/api/announcements/${id}`),
  create: (data: Partial<T.Announcement>): Promise<T.Announcement> =>
    fetchApi('/api/announcements', { method: 'POST', body: JSON.stringify(data) }),
  update: (id: string, data: Partial<T.Announcement>): Promise<T.Announcement> =>
    fetchApi(`/api/announcements/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  remove: (id: string): Promise<{ success: boolean }> =>
    fetchApi(`/api/announcements/${id}`, { method: 'DELETE' }),
};

// ── Admins ────────────────────────────────────────────────────────────────────
export const admins = {
  list: (): Promise<T.Admin[]> => fetchApi<{ admins: T.Admin[] }>('/api/admins').then((r) => r.admins),
  create: (data: { name: string; email: string; password: string; role: string }): Promise<T.Admin> =>
    fetchApi<{ admin: T.Admin }>('/api/admins', { method: 'POST', body: JSON.stringify(data) }).then((r) => r.admin),
  /** `password` resets another account's password and is Super Admin only. */
  update: (id: string, data: Partial<T.Admin> & { password?: string }): Promise<T.Admin> =>
    fetchApi<{ admin: T.Admin }>(`/api/admins/${id}`, { method: 'PATCH', body: JSON.stringify(data) }).then((r) => r.admin),
  remove: (id: string): Promise<{ success: boolean }> =>
    fetchApi(`/api/admins/${id}`, { method: 'DELETE' }),
};

// ── Audit ─────────────────────────────────────────────────────────────────────
export const audit = {
  list: (params: { search?: string; entity_type?: string; date_from?: string; date_to?: string; page?: number; limit?: number }): Promise<T.PaginatedResponse<T.AuditEvent>> => {
    const qs = new URLSearchParams();
    if (params.search) qs.set('search', params.search);
    if (params.entity_type) qs.set('entity_type', params.entity_type);
    if (params.date_from) qs.set('date_from', params.date_from);
    if (params.date_to) qs.set('date_to', params.date_to);
    if (params.page) qs.set('page', String(params.page));
    if (params.limit) qs.set('limit', String(params.limit));
    return fetchApi(`/api/audit?${qs}`);
  },
};

// ── Settings ──────────────────────────────────────────────────────────────────
export const settings = {
  get: (): Promise<T.PlatformSettings> => fetchApi('/api/settings'),
  update: (data: Partial<T.PlatformSettings>): Promise<T.PlatformSettings> =>
    fetchApi('/api/settings', { method: 'PATCH', body: JSON.stringify(data) }),
};

// ── Verification forms ────────────────────────────────────────────────────────
export const verificationForms = {
  list: (): Promise<Record<string, VerificationForm>> => fetchApi('/api/verification-forms'),
  save: (subject: FormSubject, sections: FormSection[]): Promise<VerificationForm> =>
    fetchApi(`/api/verification-forms/${subject}`, { method: 'PUT', body: JSON.stringify({ sections }) }),
};

// ── Notifications ─────────────────────────────────────────────────────────────
export const notifications = {
  list: (): Promise<T.AdminNotification[]> => fetchApi('/api/notifications'),
  markRead: (id: string): Promise<T.AdminNotification> =>
    fetchApi(`/api/notifications/${id}`, { method: 'PATCH' }),
  markAllRead: (): Promise<{ success: boolean; updated: number }> =>
    fetchApi('/api/notifications/read-all', { method: 'PATCH' }),
  /** Sends a real in-app notification to one talent or organisation. */
  send: (data: { role: 'talent' | 'org'; id: string; title: string; body?: string }): Promise<{ success: boolean }> =>
    fetchApi('/api/notifications/send', { method: 'POST', body: JSON.stringify(data) }),
};

// ── Internal notes ────────────────────────────────────────────────────────────
export interface EntityNote {
  id: string;
  entity_type: string;
  entity_id: string;
  note: string;
  created_by: string | null;
  created_at: string;
}

export const notes = {
  list: (entityType: string, entityId: string): Promise<EntityNote[]> =>
    fetchApi(`/api/notes?entity_type=${encodeURIComponent(entityType)}&entity_id=${encodeURIComponent(entityId)}`),
  create: (data: { entity_type: string; entity_id: string; note: string }): Promise<EntityNote> =>
    fetchApi('/api/notes', { method: 'POST', body: JSON.stringify(data) }),
  remove: (id: string): Promise<{ success: boolean }> =>
    fetchApi(`/api/notes/${id}`, { method: 'DELETE' }),
};

// ── Analytics ─────────────────────────────────────────────────────────────────
export const analytics = {
  dashboard: (): Promise<T.DashboardStats> => fetchApi('/api/analytics/dashboard'),
  signups: (): Promise<{ month: string; talent: number; orgs: number }[]> => fetchApi('/api/analytics/signups'),
  revenue: (): Promise<{ month: string; amount: number }[]> => fetchApi('/api/analytics/revenue'),
  categories: (): Promise<{ name: string; value: number }[]> => fetchApi('/api/analytics/categories'),
  balances: (): Promise<T.BalancesResponse> => fetchApi('/api/analytics/balances'),
};

// ── Dashboard (combined) ──────────────────────────────────────────────────────
export const dashboard = {
  stats: (): Promise<T.DashboardFullResponse> => fetchApi('/api/dashboard/stats'),
};

// ── Fees ──────────────────────────────────────────────────────────────────────
export const fees = {
  listOrgs: (): Promise<Array<{ id: string; name: string; initials: string | null; color: string | null; industry: string | null; account_status: string; custom_fee: number | null; override_fee: number | null }>> =>
    fetchApi('/api/fees/orgs'),
  updateOrgFee: (orgId: string, feePercentage: number | null): Promise<{ success: boolean }> =>
    fetchApi(`/api/fees/orgs/${orgId}`, { method: 'PATCH', body: JSON.stringify({ fee_percentage: feePercentage }) }),
};

